import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SessionUser } from "../types/auth";
import { sanitizeReturnTo } from "../utils/auth/return-to";

const SESSION_ENDPOINT = "/auth/session";

export interface AuthContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (returnTo?: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps {
  initialUser: SessionUser | null;
  children: React.ReactNode;
}

/**
 * Single source of truth for session. Fetches /auth/session at most once per app load
 * when initialUser is null, then shares the result via context so all useAuth() consumers
 * get the same user without each triggering their own request.
 */
export function AuthProvider({ initialUser, children }: AuthProviderProps) {
  const [user, setUser] = useState<SessionUser | null>(initialUser);
  const [isLoading, setIsLoading] = useState<boolean>(
    !initialUser && typeof window !== "undefined"
  );
  const hasFetchedRef = useRef<boolean>(false);

  // Sync when SSR/hydration provides a new initialUser (e.g. after login redirect).
  // When initialUser is non-null, use it. When null, do not overwrite existing user so client-side
  // navigation to a page whose pageContext has no user (e.g. tools page) does not clear the session.
  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
      hasFetchedRef.current = true;
      setIsLoading(false);
    }
  }, [initialUser?.id, initialUser]);

  // Single session fetch when we have no user and haven't fetched yet
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (initialUser || hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    let cancelled = false;
    setIsLoading(true);

    fetch(SESSION_ENDPOINT, {
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) return { user: null };
        return res.json() as Promise<{ user: SessionUser | null }>;
      })
      .then((data) => {
        if (!cancelled) setUser(data?.user ?? null);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialUser]);

  const signIn = useCallback((returnTo?: string) => {
    const target =
      returnTo ??
      `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const sanitized = sanitizeReturnTo(target);
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(sanitized)}`;
  }, []);

  const signOut = useCallback(() => {
    window.location.href = "/auth/logout";
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user),
      signIn,
      signOut,
    }),
    [user, isLoading, signIn, signOut]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
}

/** Returns auth context or undefined when outside AuthProvider. Use when hook order must stay stable (e.g. LayoutContent). */
export function useAuthOptional(): AuthContextValue | undefined {
  return useContext(AuthContext);
}

export { AuthContext };
